import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ReactNode, useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import type { SpaceMode } from "../../../../shared/schema";
import { queries } from "../../../../shared/queries";
import {
  type GrantEntry,
  grantHasPermission,
  rolesForScope,
  type RoleOption,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../../../shared/rbac/roles";
import {
  isTenantScopeGrant,
  type Permission,
} from "../../../../shared/rbac/permissions";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../../../hooks/useConnectorDisconnectImpact";
import { useCreateSkill } from "../../../hooks/useCreateSkill";
import { useCurrentUserGrants } from "../../../hooks/useCurrentUserGrants";
import { useGrantSpaceAccess } from "../../../hooks/useGrantSpaceAccess";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useGroupRemovalDisconnectImpact } from "../../../hooks/useGroupRemovalDisconnectImpact";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { useSpaceUsedSuppliers } from "../../../hooks/useSpaceUsedSuppliers";
import { getWritableScopeIds } from "../../../lib/getWritableScopeIds";
import { grantEntriesToRows } from "../../../lib/grantAssembly";
import { computeSpaceMemberSupplierGap } from "../../../lib/spaceMemberSupplierGap";
import { deriveScopeGrantDelta } from "../../../lib/spaceGrantDelta";
import { spaceIcon } from "../../../lib/spaceIcon";
import { usedSkillsForSpace } from "../../../lib/usedSuppliersForSpace";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../../lib/urls";
import { Avatar } from "../../Avatar";
import { Button } from "../../Button";
import { isInAppNavigation } from "../../Link";
import { ConnectorLogo } from "../../ConnectorLogo";
import { DeleteConnectorModalBody } from "../../Connectors/DeleteConnectorModal";
import { ConfirmModal } from "../../ConfirmModal";
import { DisconnectWarning } from "../../DisconnectWarning";
import { ConnectorLogoContainer } from "../../ConnectorLogoContainer";
import { DeleteSpaceModal } from "../../DeleteSpaceModal";
import { Dropdown } from "../../Dropdown";
import { Modal } from "../../Modal";
import { MonitoringLayout } from "../../Monitoring/MonitoringLayout";
import { Icon } from "../../Icon";
import { SpaceSettings } from "../../SpaceSettings";
import { InviteMemberModal } from "../MembersSettings/InviteMemberModal";
import { ColoredIconBadge, NeutralIconBadge, SearchField } from "../shared";
import { CreateUserGroupModal } from "../UserGroupsSettings/CreateUserGroupModal";
import { AccessGraphView } from "./AccessGraphView";
import { AccessGraphSkeleton } from "./AccessGraphSkeleton";
import {
  buildAccessModel,
  type GraphNode,
  type NodeKind,
} from "./buildAccessModel";
import { NODE_SCOPE_KIND } from "./dragGrant";
import { DropConfirmModal } from "./DropConfirmModal";
import type { MenuAction } from "./NodeContextMenu";
import {
  useMonitoringAnalytics,
  useMonitoringInteraction,
} from "../../Monitoring/analytics";

const Content = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
`;

const MapCard = styled("div")`
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* The skeleton loader drops the outline; the loaded graph keeps it. */
  &[data-loading="true"] {
    border-color: transparent;
  }
`;

const HeaderControls = styled("div")`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
`;

type GrantInput = {
  userId?: string;
  userGroupId?: string;
  serviceAccountId?: string;
  builtinRoleId?: string;
  customRoleId?: string;
};

type RawGrant = {
  userId: string | null;
  userGroupId: string | null;
  serviceAccountId: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

// The connector and group modals are tabbed; open them straight on Access.
// The space and member modals show access inline, so opening them is enough.
function openNode(node: GraphNode) {
  if (node.kind === "user") {
    navigate(routes.modal.member, { id: node.id });
  } else if (node.kind === "group") {
    navigate(routes.modal.group, { id: node.id, tab: "access" });
  } else if (node.kind === "space") {
    navigate(routes.modal.space, { id: node.id });
  } else if (node.kind === "skill") {
    navigate(routes.modal.skill, { id: node.id });
  } else {
    navigate(routes.modal.connector, { id: node.id, tab: "access" });
  }
}

// Selection lives in the ?node= param, so highlighting a card is a navigation.
function focusNode(id: string | null) {
  navigate(routes.monitoring.access, { node: id ?? "" }, { replace: true });
}

export function AccessMap() {
  const makeUrl = useMakeUrl();
  const monitoringAnalytics = useMonitoringAnalytics();
  const recordInteraction = useMonitoringInteraction();
  const connectorLibraryUrl = makeUrl(routes.connectors.library);
  // The menu's link item has no click callback, so catch the click on its way
  // down from the wrapper around the dropdown.
  const recordConnectorLinkClick = (target: EventTarget) => {
    const href =
      target instanceof Element
        ? target.closest("a")?.getAttribute("href")
        : null;
    if (href === connectorLibraryUrl) {
      recordInteraction("access.create_menu_action", { value: "connector" });
    }
  };
  const canInvite = useHasAccess("tenant:users_manage");
  const canCreateGroup = useHasAccess("tenant:user_group_create");
  const canCreateConnector = useHasAccess("tenant:connected_app_create");
  const canCreateSkill = useHasAccess("tenant:skill_create");
  const canCreateSpace = useHasAccess("tenant:space_create");
  const canAddAnything =
    canInvite ||
    canCreateGroup ||
    canCreateConnector ||
    canCreateSkill ||
    canCreateSpace;

  // The access map mirrors the list views' mutations, so it honors the same IdP
  // sync locks: deactivating/inviting users is gated on user provisioning, group
  // membership edits (drag a user onto a group, remove them) on group membership,
  // and creating/deleting groups on group lifecycle.
  const { locked: userProvisioningLocked, reason: userProvisioningReason } =
    useIdpSyncLock("userProvisioning");
  const { locked: groupMembershipLocked, reason: groupMembershipReason } =
    useIdpSyncLock("groupMembership");
  const { locked: groupLifecycleLocked, reason: groupLifecycleReason } =
    useIdpSyncLock("groupLifecycle");

  const { node: focusId, search: searchParam } = useCurrentUrl(
    routes.monitoring.access
  );
  const query = searchParam ?? "";

  const [users, usersResult] = useQuery(queries.allUsers());
  const [groups, groupsResult] = useQuery(queries.accessGraphGroups());
  const [apps, appsResult] = useQuery(queries.accessGraphConnectedApps());
  const [skills, skillsResult] = useQuery(queries.accessGraphSkills());
  const [spaces, spacesResult] = useQuery(queries.spaceAvailabilityGrants());
  const [customRoles, customRolesResult] = useQuery(queries.allRolesLite());
  const [admins, adminsResult] = useQuery(queries.tenantAdminGrants());
  const [myGrants] = useCurrentUserGrants();

  // Adding a member to a space must offer to grant the connectors and skills
  // that space uses, the same as the space members table. Without this the
  // drag-to-grant flow would silently skip that step.
  const { guard: guardSpaceAccess, modal: grantAccessModal } =
    useGrantSpaceAccess();
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();
  const manageableConnectorIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        apps.map((a) => a.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.connected_app
      ),
    [myGrants, apps]
  );
  const manageableSkillIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        skills.map((s) => s.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.skill
      ),
    [myGrants, skills]
  );

  // Render the graph only once every query has materialized, so the model and
  // layout are computed once at final size instead of reflowing as each of the
  // seven queries lands.
  const loaded = [
    usersResult,
    groupsResult,
    appsResult,
    skillsResult,
    spacesResult,
    customRolesResult,
    adminsResult,
  ].every((r) => r.type === "complete");

  const adminUserIds = useMemo(
    () =>
      new Set(
        admins.map((g) => g.userId).filter((id): id is string => Boolean(id))
      ),
    [admins]
  );

  const model = useMemo(
    () =>
      buildAccessModel({
        users,
        groups,
        apps,
        skills,
        spaces,
        adminUserIds,
        customRoles,
      }),
    [users, groups, apps, skills, spaces, adminUserIds, customRoles]
  );

  const visualById = useMemo(() => {
    const m = new Map<string, ReactNode>();
    for (const u of users)
      if (!u.deactivated)
        m.set(u.id, <Avatar user={u} size={32} tooltip={false} />);
    for (const g of groups)
      m.set(g.id, <ColoredIconBadge id={g.id} iconName="users-round" />);
    for (const a of apps)
      m.set(
        a.id,
        <ConnectorLogoContainer
          size={32}
          borderRadius={8}
          outline={false}
          hoverBackground={false}
        >
          <ConnectorLogo app={a} size={16} />
        </ConnectorLogoContainer>
      );
    for (const sk of skills)
      m.set(sk.id, <NeutralIconBadge iconName="skills" />);
    for (const s of spaces)
      if (s.mode !== "personal")
        m.set(
          s.id,
          <NeutralIconBadge iconName={spaceIcon(s.mode as SpaceMode)} />
        );
    return m;
  }, [users, groups, apps, skills, spaces]);

  const z = useZero();
  const { createSkill } = useCreateSkill();
  const [pendingDrop, setPendingDrop] = useState<{
    source: GraphNode;
    target: GraphNode;
  } | null>(null);
  // Live connector usage is a tenant-wide fan-out over every branch head, so
  // only the space a pending member drop targets loads it, while its role is
  // being picked.
  const pendingDropNodes = pendingDrop
    ? [pendingDrop.source, pendingDrop.target]
    : [];
  const pendingDropSpace = pendingDropNodes.some(
    (n) => n.kind === "user" || n.kind === "group"
  )
    ? pendingDropNodes.find((n) => n.kind === "space")
    : undefined;
  const { usedConnectorsForSpace, usedConnectorsLoaded } =
    useSpaceUsedSuppliers(apps, pendingDropSpace ? [pendingDropSpace.id] : []);
  const [pendingRemove, setPendingRemove] = useState<{
    active: GraphNode;
    target: GraphNode;
  } | null>(null);
  const [creating, setCreating] = useState<"member" | "group" | "space" | null>(
    null
  );
  const [actionConfirm, setActionConfirm] = useState<{
    title: string;
    description: string;
    confirmLabel: string;
    run: () => void;
  } | null>(null);
  const [deletingSpace, setDeletingSpace] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deletingApp, setDeletingApp] = useState<string | null>(null);

  const appById = useMemo(() => new Map(apps.map((a) => [a.id, a])), [apps]);
  const skillById = useMemo(
    () => new Map(skills.map((s) => [s.id, s])),
    [skills]
  );
  const spaceById = useMemo(
    () => new Map(spaces.map((s) => [s.id, s])),
    [spaces]
  );
  const groupById = useMemo(
    () => new Map(groups.map((g) => [g.id, g])),
    [groups]
  );
  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const pendingRemoveApp = useMemo(() => {
    if (!pendingRemove) return null;
    const { active, target } = pendingRemove;
    const app =
      active.kind === "app" ? active : target.kind === "app" ? target : null;
    if (!app) return null;
    const principal = app === active ? target : active;
    if (principal.kind !== "user" && principal.kind !== "group") return null;
    return { appId: app.id, principal };
  }, [pendingRemove]);

  const pendingRemoveMembership = useMemo(() => {
    if (!pendingRemove) return null;
    const { active, target } = pendingRemove;
    if (active.kind === "user" && target.kind === "group")
      return { groupId: target.id, userId: active.id };
    if (active.kind === "group" && target.kind === "user")
      return { groupId: active.id, userId: target.id };
    return null;
  }, [pendingRemove]);

  const computeConnectorImpact = useConnectorDisconnectImpact(
    pendingRemoveApp?.appId ?? null
  );
  const computeGroupImpact = useGroupRemovalDisconnectImpact(
    useMemo(
      () => (pendingRemoveMembership ? [pendingRemoveMembership.groupId] : []),
      [pendingRemoveMembership]
    )
  );

  const contextVisual = (node: GraphNode): ReactNode => {
    if (node.kind === "user") {
      const u = userById.get(node.id);
      return u ? <Avatar user={u} size={16} tooltip={false} /> : null;
    }
    if (node.kind === "group")
      return <Icon name="users-round" size={16} color="var(--text-primary)" />;
    if (node.kind === "skill")
      return <Icon name="skills" size={16} color="var(--text-primary)" />;
    if (node.kind === "space") {
      const s = spaceById.get(node.id);
      return (
        <Icon
          name={s ? spaceIcon(s.mode as SpaceMode) : "hash"}
          size={16}
          color="var(--text-primary)"
        />
      );
    }
    const a = appById.get(node.id);
    return a ? <ConnectorLogo app={a} size={16} /> : null;
  };

  // The target scope's current grants, so the drop's delta is computed against
  // them (deriveScopeGrantDelta) rather than replacing the whole list.
  const scopeGrants = (kind: NodeKind, id: string): GrantInput[] => {
    const row =
      kind === "app"
        ? appById.get(id)
        : kind === "skill"
          ? skillById.get(id)
          : spaceById.get(id);
    const raw = (row?.roleGrantsAsScope ??
      []) as unknown as readonly RawGrant[];
    return raw.flatMap((g) =>
      g.userId || g.userGroupId || g.serviceAccountId
        ? [
            {
              userId: g.userId ?? undefined,
              userGroupId: g.userGroupId ?? undefined,
              serviceAccountId: g.serviceAccountId ?? undefined,
              builtinRoleId: g.builtinRoleId ?? undefined,
              customRoleId: g.customRoleId ?? undefined,
            },
          ]
        : []
    );
  };

  // A group's current members, so dropping a user onto it adds to them rather
  // than replacing (setGroupGrants sets the full member list).
  const groupMemberGrants = (groupId: string): GrantInput[] => {
    const raw = (groupById.get(groupId)?.roleGrantsAsScope ??
      []) as unknown as readonly RawGrant[];
    return raw.flatMap((g) =>
      g.userId
        ? [
            {
              userId: g.userId,
              builtinRoleId: g.builtinRoleId ?? undefined,
              customRoleId: g.customRoleId ?? undefined,
            },
          ]
        : []
    );
  };

  // A principal dropped on a scope is granted a role there; a connector/skill
  // dropped on a space (or vice versa) is granted to all the space's members;
  // a user dropped on a group is added as a member (its own mutator).
  const resolveDrop = (source: GraphNode, target: GraphNode) => {
    const roleFields = (role: RoleOption): GrantInput =>
      role.builtinRoleId
        ? { builtinRoleId: role.builtinRoleId }
        : { customRoleId: role.customRoleId };
    const customRoleList = customRoles.map((r) => ({
      id: r.id,
      name: r.name,
      scopeKind: r.scopeKind,
    }));

    if (source.kind === "user" && target.kind === "group") {
      const existing = groupMemberGrants(target.id);
      return {
        title: `Add ${source.name} to ${target.name}?`,
        description: `Choose the role ${source.name} gets in ${target.name}.`,
        confirmLabel: "Add member",
        confirmReady: true,
        roleOptions: rolesForScope("user_group", customRoleList),
        commit: (role: RoleOption) =>
          z.mutate(
            sharedMutators.setGroupGrants({
              analytics: monitoringAnalytics(),
              userGroupId: target.id,
              grants: [...existing, { userId: source.id, ...roleFields(role) }],
            })
          ),
      };
    }

    const sourceIsPrincipal = source.kind === "user" || source.kind === "group";
    const targetIsPrincipal = target.kind === "user" || target.kind === "group";

    let scope: GraphNode;
    let principals: GrantInput[];
    let title: string;
    let description: string;

    if (sourceIsPrincipal !== targetIsPrincipal) {
      const principal = sourceIsPrincipal ? source : target;
      scope = sourceIsPrincipal ? target : source;
      principals = [
        principal.kind === "user"
          ? { userId: principal.id }
          : { userGroupId: principal.id },
      ];
      title = `Give ${principal.name} access to ${scope.name}?`;
      description = `Choose the role ${principal.name} gets on ${scope.name}.`;
    } else {
      const resource = source.kind === "space" ? target : source;
      const space = source.kind === "space" ? source : target;
      scope = resource;
      const spaceRaw = (spaceById.get(space.id)?.roleGrantsAsScope ??
        []) as unknown as readonly RawGrant[];
      principals = spaceRaw.flatMap<GrantInput>((g) =>
        g.userId
          ? [{ userId: g.userId }]
          : g.userGroupId
            ? [{ userGroupId: g.userGroupId }]
            : []
      );
      title = `Make ${resource.name} available in ${space.name}?`;
      description = `Everyone with access to ${space.name} gets this role on ${resource.name}.`;
    }

    const scopeKind = NODE_SCOPE_KIND[scope.kind]!;
    const existing = scopeGrants(scope.kind, scope.id);
    // Adding a principal to a space is a space-member change, so it needs the
    // connector/skill gap prompt (space is the only cross-resource dependent);
    // granting a whole space's members onto a supplier (the else branch above)
    // has no gap to check. Either way the write is one atomic patchScopeGrants.
    const isSpaceMemberAdd =
      scope.kind === "space" && sourceIsPrincipal !== targetIsPrincipal;
    return {
      title,
      description,
      confirmLabel: "Grant access",
      // The supplier gap check reads the space's live connector usage.
      confirmReady: !isSpaceMemberAdd || usedConnectorsLoaded,
      roleOptions: rolesForScope(scopeKind, customRoleList),
      commit: (role: RoleOption) => {
        const desired: GrantEntry[] = [
          ...existing,
          ...principals.map((p) => ({ ...p, ...roleFields(role) })),
        ];
        const doMutate = () => {
          const { add, remove } = deriveScopeGrantDelta(existing, desired);
          z.mutate(
            sharedMutators.patchScopeGrants({
              analytics: monitoringAnalytics(),
              scopeId: scope.id,
              scopeKind,
              add,
              remove,
              autoGrantConnectors: scopeKind === "space",
              autoGrantSkills: scopeKind === "space",
            })
          );
        };
        if (!isSpaceMemberAdd) {
          doMutate();
          return;
        }
        // The space's grants carry customRole.rolePermissions (see
        // spaceAvailabilityGrants), which the gap/tier computation needs.
        const spaceGrants = spaceById.get(scope.id)?.roleGrantsAsScope ?? [];
        const gaps = computeSpaceMemberSupplierGap({
          currentSpaceGrants: spaceGrants,
          desiredSpaceGrants: grantEntriesToRows(desired, customRoles),
          connectors: usedConnectorsForSpace(scope.id),
          skills: usedSkillsForSpace(spaceGrants, skills),
          memberships: userGroupMemberships,
          manageableConnectorIds,
          manageableSkillIds,
        });
        guardSpaceAccess({ spaceName: scope.name, gaps, proceed: doMutate });
      },
    };
  };

  // The active node and a node it directly connects to: removing a membership
  // takes the user out of the group; removing a direct grant drops the
  // principal from the scope. Either way we resend the full list minus them.
  const resolveRemove = (active: GraphNode, target: GraphNode) => {
    const activeIsPrincipal = active.kind === "user" || active.kind === "group";

    if (
      (active.kind === "user" && target.kind === "group") ||
      (active.kind === "group" && target.kind === "user")
    ) {
      const user = active.kind === "user" ? active : target;
      const group = active.kind === "group" ? active : target;
      return {
        title: `Remove ${user.name} from ${group.name}?`,
        description: `They’ll lose any access they get through ${group.name}.`,
        commit: () =>
          z.mutate(
            sharedMutators.setGroupGrants({
              analytics: monitoringAnalytics(),
              userGroupId: group.id,
              grants: groupMemberGrants(group.id).filter(
                (g) => g.userId !== user.id
              ),
            })
          ),
      };
    }

    const scope = activeIsPrincipal ? target : active;
    const principal = activeIsPrincipal ? active : target;
    const scopeKind = NODE_SCOPE_KIND[scope.kind]!;
    const principalField =
      principal.kind === "user"
        ? { userId: principal.id }
        : { userGroupId: principal.id };
    return {
      title: `Remove ${principal.name}’s access to ${scope.name}?`,
      description: `${principal.name} will no longer have access to ${scope.name}.`,
      commit: () =>
        z.mutate(
          sharedMutators.patchScopeGrants({
            analytics: monitoringAnalytics(),
            scopeId: scope.id,
            scopeKind,
            add: [],
            remove: [principalField],
          })
        ),
    };
  };

  // A viewer sees the graph filtered to what they can read; editing follows
  // their actual permissions — whether they can manage the target scope's
  // grants (a group's membership for a user dropped onto it).
  const hasPermission = (permission: Permission, scopeId?: string): boolean => {
    const tenantScope = !scopeId;
    const admin =
      !tenantScope &&
      myGrants.some(
        (g) => isTenantScopeGrant(g) && grantHasPermission(g, "allow_all")
      );
    return (
      admin ||
      myGrants
        .filter((g) =>
          tenantScope ? isTenantScopeGrant(g) : g.scopeId === scopeId
        )
        .some((g) => grantHasPermission(g, permission))
    );
  };

  // Mirrors resolveDrop's scope resolution so the drop affordance matches the
  // permission the resulting mutation will require.
  const canGrant = (source: GraphNode, target: GraphNode): boolean => {
    if (source.kind === "user" && target.kind === "group")
      return hasPermission("user_group:write", target.id);
    const sourceIsPrincipal = source.kind === "user" || source.kind === "group";
    const targetIsPrincipal = target.kind === "user" || target.kind === "group";
    const scope =
      sourceIsPrincipal !== targetIsPrincipal
        ? sourceIsPrincipal
          ? target
          : source
        : source.kind === "space"
          ? target
          : source;
    const scopeKind = NODE_SCOPE_KIND[scope.kind];
    return scopeKind
      ? hasPermission(SCOPE_MANAGE_GRANTS_PERMISSION[scopeKind], scope.id)
      : false;
  };

  // The management actions a node's more-menu offers, mirroring each list
  // view's row menu, each gated by the viewer's permission on that node.
  const nodeActions = (node: GraphNode): MenuAction[] =>
    nodeActionsForNode(node).map((action) => ({
      ...action,
      onClick: () => {
        recordInteraction("access.context_menu_action", {
          targetType: node.kind,
          targetId: node.id,
          value: action.key,
        });
        action.onClick();
      },
    }));

  const nodeActionsForNode = (node: GraphNode): MenuAction[] => {
    if (node.kind === "user") {
      if (!hasPermission("tenant:users_manage")) return [];
      return [
        {
          key: "deactivate",
          icon: "trash",
          label: "Deactivate",
          destructive: true,
          disabled: userProvisioningLocked,
          disabledReason: userProvisioningReason ?? undefined,
          onClick: () =>
            setActionConfirm({
              title: "Deactivate user",
              description: `${node.name} will be deactivated and signed out immediately.`,
              confirmLabel: "Deactivate",
              run: () =>
                z.mutate(
                  sharedMutators.deactivateUser({
                    analytics: monitoringAnalytics(),
                    userId: node.id,
                  })
                ),
            }),
        },
      ];
    }
    if (node.kind === "group") {
      if (!hasPermission("user_group:write", node.id)) return [];
      return [
        {
          key: "delete",
          icon: "trash",
          label: "Delete",
          destructive: true,
          disabled: groupLifecycleLocked,
          disabledReason: groupLifecycleReason ?? undefined,
          onClick: () =>
            setActionConfirm({
              title: "Delete group",
              description: `“${node.name}” will be permanently deleted, along with all of its role grants.`,
              confirmLabel: "Delete",
              run: () =>
                z.mutate(
                  sharedMutators.deleteUserGroup({
                    analytics: monitoringAnalytics(),
                    userGroupId: node.id,
                  })
                ),
            }),
        },
      ];
    }
    if (node.kind === "skill") {
      if (!hasPermission("skill:write", node.id)) return [];
      return [
        {
          key: "delete",
          icon: "trash",
          label: "Delete",
          destructive: true,
          onClick: () =>
            setActionConfirm({
              title: "Delete skill?",
              description:
                "Permanently deletes this skill and its access settings.",
              confirmLabel: "Delete",
              run: () =>
                z.mutate(
                  sharedMutators.deleteSkill({
                    analytics: monitoringAnalytics(),
                    skillId: node.id,
                  })
                ),
            }),
        },
      ];
    }
    if (node.kind === "space") {
      if (!hasPermission("space:manage", node.id)) return [];
      return [
        {
          key: "delete",
          icon: "trash",
          label: "Delete",
          destructive: true,
          onClick: () => setDeletingSpace({ id: node.id, name: node.name }),
        },
      ];
    }
    if (!hasPermission("connected_app:write", node.id)) return [];
    return [
      {
        key: "delete",
        icon: "trash",
        label: "Delete",
        destructive: true,
        onClick: () => setDeletingApp(node.id),
      },
    ];
  };

  const removalImpact = pendingRemoveApp
    ? computeConnectorImpact(
        appById.get(pendingRemoveApp.appId)?.roleGrantsAsScope ?? [],
        scopeGrants("app", pendingRemoveApp.appId).filter((g) =>
          pendingRemoveApp.principal.kind === "user"
            ? g.userId !== pendingRemoveApp.principal.id
            : g.userGroupId !== pendingRemoveApp.principal.id
        )
      )
    : pendingRemoveMembership
      ? computeGroupImpact(pendingRemoveMembership.groupId, [
          pendingRemoveMembership.userId,
        ])
      : null;
  const removalChatCount = useAffectedChatCount(
    pendingRemoveApp?.appId ?? null,
    removalImpact?.affectedUserIds ?? []
  );

  const resolved = pendingDrop
    ? resolveDrop(pendingDrop.source, pendingDrop.target)
    : null;
  const resolvedRemove = pendingRemove
    ? resolveRemove(pendingRemove.active, pendingRemove.target)
    : null;

  return (
    <MonitoringLayout
      headerItem={
        <HeaderControls>
          <SearchField
            placeholder="Search the access map…"
            value={query}
            onChange={(value) =>
              navigate(
                routes.monitoring.access,
                { search: value },
                { replace: true }
              )
            }
          />
          {canAddAnything && (
            <div
              style={{ display: "contents" }}
              onPointerDownCapture={(e) => {
                if (isInAppNavigation(e)) recordConnectorLinkClick(e.target);
              }}
              onClickCapture={(e) => {
                if (e.detail === 0) recordConnectorLinkClick(e.target);
              }}
            >
              <Dropdown
                align="bottom"
                minWidth={180}
                opener={<Button size="l" icon="plus" />}
                sections={[
                  {
                    label: "Add…",
                    items: [
                      ...(canInvite
                        ? [
                            {
                              type: "button" as const,
                              left: <Icon name="id-card" size={16} />,
                              label: "Member",
                              disabled: userProvisioningLocked,
                              title: userProvisioningReason ?? undefined,
                              onClick: () => {
                                recordInteraction("access.create_menu_action", {
                                  value: "member",
                                });
                                setCreating("member");
                              },
                            },
                          ]
                        : []),
                      ...(canCreateGroup
                        ? [
                            {
                              type: "button" as const,
                              left: <Icon name="users-round" size={16} />,
                              label: "Group",
                              disabled: groupLifecycleLocked,
                              title: groupLifecycleReason ?? undefined,
                              onClick: () => {
                                recordInteraction("access.create_menu_action", {
                                  value: "group",
                                });
                                setCreating("group");
                              },
                            },
                          ]
                        : []),
                      ...(canCreateConnector
                        ? [
                            {
                              type: "link" as const,
                              left: <Icon name="api-key-custom" size={16} />,
                              label: "Connector",
                              to: makeUrl(routes.connectors.library),
                            },
                          ]
                        : []),
                      ...(canCreateSkill
                        ? [
                            {
                              type: "button" as const,
                              left: <Icon name="skills" size={16} />,
                              label: "Skill",
                              onClick: () => {
                                recordInteraction("access.create_menu_action", {
                                  value: "skill",
                                });
                                const id = createSkill();
                                if (id)
                                  navigate(routes.modal.skill, {
                                    id,
                                    tab: "files",
                                  });
                              },
                            },
                          ]
                        : []),
                      ...(canCreateSpace
                        ? [
                            {
                              type: "button" as const,
                              left: <Icon name="hash" size={16} />,
                              label: "Space",
                              onClick: () => {
                                recordInteraction("access.create_menu_action", {
                                  value: "space",
                                });
                                setCreating("space");
                              },
                            },
                          ]
                        : []),
                    ],
                  },
                ]}
              />
            </div>
          )}
        </HeaderControls>
      }
    >
      <Content>
        <MapCard data-loading={!loaded}>
          {loaded ? (
            <AccessGraphView
              model={model}
              visualById={visualById}
              contextVisual={contextVisual}
              query={query}
              focusId={focusId}
              onFocus={focusNode}
              onSelect={(node) => {
                recordInteraction("access.node_click", {
                  targetType: node.kind,
                  targetId: node.id,
                });
                openNode(node);
              }}
              onDrop={(source, target) => setPendingDrop({ source, target })}
              onRemove={(active, target) =>
                setPendingRemove({ active, target })
              }
              nodeActions={nodeActions}
              membershipLocked={groupMembershipLocked}
              membershipReason={groupMembershipReason}
              canGrant={canGrant}
            />
          ) : (
            <AccessGraphSkeleton />
          )}
        </MapCard>
      </Content>

      {resolved && (
        <DropConfirmModal
          title={resolved.title}
          description={resolved.description}
          confirmLabel={resolved.confirmLabel}
          confirmReady={resolved.confirmReady}
          roleOptions={resolved.roleOptions}
          onClose={() => setPendingDrop(null)}
          onConfirm={(role) => {
            resolved.commit(role);
            setPendingDrop(null);
          }}
        />
      )}

      {grantAccessModal}

      {resolvedRemove && (
        <ConfirmModal
          open
          destructive
          title={resolvedRemove.title}
          description={resolvedRemove.description}
          confirmLabel="Remove"
          onClose={() => setPendingRemove(null)}
          onConfirm={() => {
            resolvedRemove.commit();
            setPendingRemove(null);
          }}
        >
          {removalImpact && (
            <DisconnectWarning
              {...removalImpact}
              chatCount={removalChatCount}
            />
          )}
        </ConfirmModal>
      )}

      <InviteMemberModal
        open={creating === "member"}
        onClose={() => setCreating(null)}
        customRoles={customRoles}
      />
      <CreateUserGroupModal
        open={creating === "group"}
        onClose={() => setCreating(null)}
      />
      {creating === "space" && (
        <SpaceSettings
          mode="create"
          onCreated={() => setCreating(null)}
          onClose={() => setCreating(null)}
        />
      )}

      {actionConfirm && (
        <ConfirmModal
          open
          destructive
          title={actionConfirm.title}
          description={actionConfirm.description}
          confirmLabel={actionConfirm.confirmLabel}
          onClose={() => setActionConfirm(null)}
          onConfirm={() => {
            actionConfirm.run();
            setActionConfirm(null);
          }}
        />
      )}
      {deletingSpace && (
        <DeleteSpaceModal
          spaceId={deletingSpace.id}
          spaceName={deletingSpace.name}
          onClose={() => setDeletingSpace(null)}
        />
      )}
      {deletingApp && (
        <Modal open alert onClose={() => setDeletingApp(null)}>
          <DeleteConnectorModalBody
            appId={deletingApp}
            onClose={() => setDeletingApp(null)}
          />
        </Modal>
      )}
    </MonitoringLayout>
  );
}
